import {
  useActivity,
  useActivityOccurrence,
  type IActivity,
  type IOneTimeActivitySchedule,
} from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import styles from "./Activity.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import * as Sentry from "@sentry/react";
import { IconButton } from "@medoma/components/Button/Button";
import CrossIcon from "@medoma/components/icons/CrossIcon";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { ActivityInformation } from "./ActivityInformation";
import { CloseActivitySeriesButton } from "../ActivityOccurrence/CloseActivitySeriesButton";
import { useQueryClient } from "@tanstack/react-query";
import { RemoveActivityOccurrence } from "../ActivityOccurrence/RemoveActivityOccurrence";
import { getActivityOccurrenceIdFromActivitySchedule } from "./getActivityOccurrenceIdFromActivitySchedule";
import { useLingui } from "@lingui/react/macro";

const Activity = () => {
  const { t } = useLingui();
  const { activityId } = z
    .object({ activityId: z.string() })
    .parse(useParams());

  const navigate = useNavigate();

  const { data: activity, isPending, isError, error } = useActivity(activityId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }

  return (
    <div className={styles.activity}>
      <header className={styles.header}>
        <CategoryIcon category={activity.category} size="small" />
        <Heading level="h2">{activity.title}</Heading>
        <IconButton aria-label={t`Stäng`} onClick={() => navigate("..")}>
          <CrossIcon />
        </IconButton>
      </header>
      {activity.recurring ? (
        <RemoveRecurringActivityButton activity={activity} />
      ) : "date" in activity.schedule ? (
        <RemoveOneTimeActivityButton
          activityId={activityId}
          schedule={activity.schedule}
        />
      ) : null}
      <ActivityInformation activityId={activityId} />
    </div>
  );
};

const RemoveRecurringActivityButton = ({
  activity,
}: {
  activity: IActivity;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return (
    <CloseActivitySeriesButton
      id={activity.id}
      status={activity.status}
      onSuccess={async () => {
        void queryClient.invalidateQueries();
        void navigate("..");
      }}
    />
  );
};

const RemoveOneTimeActivityButton = ({
  activityId,
  schedule,
}: {
  activityId: string;
  schedule: IOneTimeActivitySchedule;
}) => {
  const occurrenceId = getActivityOccurrenceIdFromActivitySchedule(schedule);

  const {
    data: activityOccurrence,
    isPending,
    isError,
  } = useActivityOccurrence(activityId, occurrenceId);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // If the activity occurrence is not found, we can't remove it, and it makes sense to not show the button.
  if (isPending || isError) {
    return null;
  }

  return (
    <RemoveActivityOccurrence
      id={occurrenceId}
      activityId={activityOccurrence.activityId}
      status={activityOccurrence.status}
      onSuccess={async () => {
        void queryClient.invalidateQueries();
        void navigate("..");
      }}
    />
  );
};

export default Activity;
